// src/pages/ProfilePage/ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ProfilePage.css';
import { useNavigate } from 'react-router-dom';
import { COMPETITION_END_DATE } from '../../config/config';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [files, setFiles] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [isCompetitionOpen, setCompetitionOpen] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isEditingTeam, setIsEditingTeam] = useState(false);
  const [tempTeam, setTempTeam] = useState("");

  useEffect(() => {
    const currentDate = new Date();
    if (currentDate >= COMPETITION_END_DATE) setCompetitionOpen(false);

    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
        setSubmissions(res.data.submissions || []);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setFetchError('Error fetching profile. Please log in again.');
        if (err.response?.status === 401) navigate('/login');
      } finally { setLoading(false); }
    };
    fetchProfile();
  }, [navigate]);

  const handleFileChange = (e) => setFiles(e.target.files);

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (isUploading) return; // 🧠 Prevent double submission

    // 🚫 Max submissions check
    if (submissions.length >= 3) {
      setUploadError("You can only have up to 3 submissions. Please delete an old one before uploading a new submission.");
      return;
    }

    if (!files || files.length === 0) {
      setUploadError('Please select up to 2 images and 1 PDF.');
      return;
    }

    const filesArray = Array.from(files);
    if (filesArray.length > 3) {
      setUploadError('You can upload a maximum of 3 files.');
      return;
    }

    let imageCount = 0, pdfCount = 0;
    for (let f of filesArray) {
      if (f.size > 10.5 * 1024 * 1024) {
        setUploadError('Each file must be less than 10MB.');
        return;
      }
      if (f.type === 'application/pdf') pdfCount++;
      else if (f.type === 'image/jpeg') imageCount++;
      else {
        setUploadError('Only JPG/JPEG or PDF allowed.');
        return;
      }
    }
    if (imageCount > 2) { setUploadError('Max 2 JPG/JPEG images.'); return; }
    if (pdfCount > 1) { setUploadError('Only 1 PDF allowed.'); return; }

    // 🚫 Duplicate name check (based on current submissions)
    const existingFileNames = submissions.flatMap(sub =>
      sub.files.map(f => f.key.split('/').pop().toLowerCase())
    );

    const duplicateFile = filesArray.find(f =>
      existingFileNames.includes(f.name.toLowerCase())
    );

    if (duplicateFile) {
      setUploadError(`You already submitted a file named "${duplicateFile.name}". Please rename your file or delete the previous one.`);
      return;
    }

    const formData = new FormData();
    filesArray.forEach(f => formData.append('files', f));

    try {
      setIsUploading(true);
      setUploadProgress(0);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/user/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        },
      });

      setSubmissions(prev => [...prev, res.data.submission]);
      setUploadSuccess('Files uploaded successfully!');
      setUploadError('');
      setFiles(null);
    } catch (err) {
      console.error('File upload error:', err);
      setUploadError('Error uploading files. Please try again.');
      setUploadSuccess('');
    }

    setTimeout(() => {
      setIsUploading(false);
      setUploadProgress(0);
    }, 600);
  };

  const handleDelete = async (submissionId, fileKey) => {
    const token = localStorage.getItem('token');
    if (!token) { setUploadError('User not authenticated'); navigate('/login'); return; }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/user/deleteSub`, {
        data: { submissionId, fileKey },
        headers: { Authorization: `Bearer ${token}` },
      });

      setSubmissions(prev =>
        prev.map(s =>
          s._id === submissionId ? { ...s, files: s.files.filter(f => f.key !== fileKey) } : s
        ).filter(s => s.files.length > 0)
      );
    } catch (err) {
      console.error('Error deleting file:', err);
      alert('Failed to delete file.');
    }
  };

  const handleSaveTeam = async () => {
    if (!isEditingTeam) return;
    setIsEditingTeam(false);

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user/update-team-members`,
        {
          params: { teamMembers: tempTeam },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setUser((prev) => ({
        ...prev,
        teamMembers: res.data.teamMembers,
      }));
    } catch (err) {
      console.error("Error updating team members:", err);
      alert("Failed to update team members.");
    }
  };

  const handleSignOut = () => { localStorage.removeItem('token'); navigate('/login'); };

  if (loading) return <div>Loading...</div>;
  if (fetchError) return <div className="error-message">{fetchError}</div>;
  if (!user) return null;

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h2>Welcome to your submission portal!</h2>
        <h2>{user.firstName} {user.lastName}'s Profile</h2>
        <p>Email: {user.email}</p>

        <div className="team-members-section">
          <label>Team Members:</label>
          {isEditingTeam ? (
            <input
              type="text"
              value={tempTeam || ""}
              onChange={(e) => setTempTeam(e.target.value)}
              onBlur={handleSaveTeam}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveTeam();
                if (e.key === "Escape") setIsEditingTeam(false);
              }}
              autoFocus
              className="team-members-input"
            />
          ) : (
            <span
              className="team-members-display"
              onClick={() => {
                setTempTeam(user.teamMembers || "");
                setIsEditingTeam(true);
              }}
              title="Click to edit team members"
            >
              {user.teamMembers || "Click to add team members"}
              <span className="edit-icon"> ✏️</span>
            </span>
          )}
        </div>
      </div>

      <button onClick={handleSignOut} className="sign-out-button">Sign Out</button>

      <div className="submissions-section">
        <h3>Previous Submissions</h3>
        {submissions.length > 0 ? (
          <ul className="submission-list">
            {submissions.map((sub, index) => (
              <li key={sub._id}>
                {sub.files.map((f, fileIndex) => {
                  const fileName = f.key.split('/').pop();
                  return (
                    <div key={fileIndex}>
                      <a href={f.url} target="_blank" rel="noopener noreferrer" download>
                        Download Submission {index + 1} - {fileName}
                      </a>
                      <button
                        className="delete-button"
                        onClick={() => handleDelete(sub._id, f.key)}
                        style={{ marginLeft: '8px' }}
                      >
                        🗑️
                      </button>
                    </div>
                  );
                })}
              </li>
            ))}
          </ul>
        ) : (
          <p>No previous submissions found.</p>
        )}
      </div>

      {isCompetitionOpen && (
        <div className="upload-section">
          <h4>Submit up to 2 JPG/JPEG and 1 PDF File (Each Max 10MB)</h4>
          {uploadSuccess && <div className="success-message">{uploadSuccess}</div>}
          {uploadError && <div className="error-message">{uploadError}</div>}
          <form onSubmit={handleFileUpload}>
            <input
              type="file"
              accept=".jpg,.jpeg,.pdf"
              multiple
              onChange={(e) => setFiles(e.target.files)}
              required
            />
<button type="submit" disabled={isUploading}>
  {isUploading ? "Uploading..." : "Submit File"}
</button>
            {isUploading && uploadProgress === 0 && (
              <div className="text-gray-500 italic">Preparing upload...</div>
            )}
            {isUploading && (
              <div className="upload-progress-container">
                <div
                  className="upload-progress-bar"
                  style={{ width: `${uploadProgress}%` }}
                >
                  {uploadProgress}%
                </div>
              </div>
            )}
          </form>
        </div>
      )}

      {!isCompetitionOpen && (
        <div className="competition-closed-message">
          <h4>Submission Closed</h4>
          <p>The competition has ended, and submissions are no longer accepted.</p>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
